import { ClassNames, css } from "@emotion/react";
import { filter, pipe } from "callbag-basics";
import subscribe from "callbag-subscribe";
import { FC, useEffect, useState } from "react";

import { WarningFilled } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  Icon,
  Modal,
  ModalPortal,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { ITicket } from "interfaces/entities/ticket";
import { actions$ } from "store/action-stream";
import { RequestAction } from "store/entities/actions";
import { IAgent } from "store/entities/agents/interfaces";
import { ITag } from "store/entities/tags/interfaces";
import { ITeam } from "store/entities/teams/interfaces";
import { TICKET } from "store/entities/tickets/actions";
import { IFetchTicketsToMergePayload } from "store/entities/tickets/interfaces";
import { IAction } from "store/helper";
import { IMergeTicketsPayload } from "store/views/tickets/interfaces";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

import { TicketsToMerge } from "./TicketsToMerge";
import { TicketsToMergeSummary } from "./TicketsToMergeSummary";

interface IMergeTicketModalProps {
  ticket: ITicket;
  ticketsToMerge: ITicket[];
  onClose(): void;
  mergeTickets(payload: IMergeTicketsPayload): void;
  fetchTicketsToMerge(payload: IFetchTicketsToMergePayload): void;
  tags: ITag[];
  teams: ITeam[];
  agents: IAgent[];
}

enum MergeModalPages {
  One,
  Two,
}

const MergeTicketModal: FC<IMergeTicketModalProps> = ({
  ticket,
  ticketsToMerge,
  onClose,
  mergeTickets,
  fetchTicketsToMerge,
  tags,
  teams,
  agents,
}) => {
  const [selectedChildTicketsIds, setChildTicketIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<MergeModalPages>(MergeModalPages.One);

  useEffect(() => {
    setIsLoading(true);
    fetchTicketsToMerge({ ticketId: ticket.id, requesterEmail: ticket.requester.email });
  }, [fetchTicketsToMerge, ticket.id, ticket.requester.email]);

  useEffect(() => {
    const unsubscribe = pipe(
      actions$,
      filter((action: IAction) =>
        [
          TICKET.FETCH_TICKETS_TO_MERGE[RequestAction.SUCCESS],
          TICKET.FETCH_TICKETS_TO_MERGE[RequestAction.FAILURE],
        ].includes(action.type)
      ),
      subscribe(() => {
        setIsLoading(false);
      })
    );

    return unsubscribe;
  }, [isLoading]);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleModalClose = () => {
    trackEngagement("Ticket: Merging modal closed without action", EventPlace.Tickets);
    onClose();
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleMergeTickets = () => {
    trackEngagement("Ticket: Merging tickets started", EventPlace.Tickets, {
      childTickets: selectedChildTicketsIds.length,
    });
    mergeTickets({ parentTicketId: ticket.id, childTicketIds: selectedChildTicketsIds, onFinish: onClose });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleNextPage = () => {
    trackEngagement("Ticket: Merging tickets navigated to second page", EventPlace.Tickets, {
      childTickets: selectedChildTicketsIds.length,
    });
    setPage(MergeModalPages.Two);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handlePrevPage = () => {
    trackEngagement("Ticket: Merging tickets navigated back to first page", EventPlace.Tickets);
    setPage(MergeModalPages.One);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const FirstPageFooter = () => (
    <Flex alignItems="center" width="100%" justifyContent="space-between">
      <Text>Step 1 of 2</Text>
      <Flex gap={withVar(SpacingToken.Spacing2)}>
        <Button size="compact" kind="secondary" onClick={handleModalClose}>
          Cancel
        </Button>
        <Button
          data-testid="merge-ticket-modal-first-page-footer-button"
          size="compact"
          kind="primary"
          onClick={handleNextPage}
          disabled={!selectedChildTicketsIds.length}
        >
          Continue
        </Button>
      </Flex>
    </Flex>
  );

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const SecondPageFooter = () => (
    <Flex css={secondPageFooterStyles} alignItems="center" width="100%" justifyContent="space-between">
      <Flex alignItems="center" gap={withVar(SpacingToken.Spacing1)}>
        <Text css={{ marginRight: withVar(SpacingToken.Spacing3) }}>Step 2 of 2</Text>
        <Icon size="large" source={WarningFilled} customColor={withVar(DesignToken.ActionWarningDefault)} />
        <Text>Merging is irreversible</Text>
      </Flex>
      <Flex gap={withVar(SpacingToken.Spacing2)}>
        <Button size="compact" kind="secondary" onClick={handlePrevPage}>
          Back
        </Button>
        <Button
          data-testid="merge-ticket-modal-second-page-footer-button"
          size="compact"
          kind="primary"
          onClick={handleMergeTickets}
          disabled={!selectedChildTicketsIds.length}
        >
          Merge tickets
        </Button>
      </Flex>
    </Flex>
  );

  return (
    <ClassNames>
      {/* TODO: Fix this the next time the file is edited. */}
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {({ css }: any) => (
        <ModalPortal zIndex={StackingContextLevel.Modal}>
          <Modal
            heading="Merge requester's tickets"
            onClose={onClose}
            footer={page === MergeModalPages.One ? <FirstPageFooter /> : <SecondPageFooter />}
            contentClassName={css({
              width: "640px",
              maxWidth: "100%",
              maxHeight: "520px",
            })}
          >
            {page === MergeModalPages.One && (
              <TicketsToMerge
                selectedChildTicketsIds={selectedChildTicketsIds}
                setChildTickets={setChildTicketIds}
                ticket={ticket}
                ticketsToMerge={ticketsToMerge}
                isLoading={isLoading}
              />
            )}
            {page === MergeModalPages.Two && (
              <TicketsToMergeSummary
                selectedChildTicketsIds={selectedChildTicketsIds}
                setChildTickets={setChildTicketIds}
                ticket={ticket}
                ticketsToMerge={ticketsToMerge}
                isLoading={isLoading}
                tags={tags}
                teams={teams}
                agents={agents}
              />
            )}
          </Modal>
        </ModalPortal>
      )}
    </ClassNames>
  );
};

const secondPageFooterStyles = css`
  position: relative;
`;

export default MergeTicketModal;
